import React, { useState, useEffect, useRef, useMemo } from 'react';
import BaseModal from '../BaseModal';
import Icon from '@mdi/react';
import { mdiEmailOutline, mdiEmailFastOutline } from '@mdi/js';
import webApiClient from '../../api/client';
import EmailTemplateEditor from './EmailTemplateEditor';
import { getEmailMode, tokensToHandlebars } from '../../utils/emailTokens';
import './EmailReviewModal.css';

/**
 * Mode-aware review modal for school-voice emails before sending.
 *
 * Renders one of three branches based on getEmailMode(emailType):
 *   - mode3 — Editable subject + EmailTemplateEditor + live preview iframe.
 *             onConfirm(subject, bodyHtml) — positional, back-compat for
 *             existing AdminPaymentModal / ClassTermsPage callers.
 *   - mode2a — Reason textarea only (no template preview).
 *              onConfirm({ reason })
 *   - mode2b — Preview iframe only (no edit fields), recipient count copy.
 *              onConfirm({})
 *
 * Mode 1 emails do not require review — opening this modal for one is a bug.
 */
const EmailReviewModal = ({
    isOpen,
    emailType,
    templateData,
    onConfirm,
    onCancel,
    isConfirming,
    dangerConfirm,
    warningMessage,
    confirmingLabel,
    recipientCount,
    audienceDescription,
}) => {
    // Block backdrop / Esc dismissal while a send is in flight. BaseModal
    // forwards onClose to backdrop click + Esc — guarding here prevents the
    // user from accidentally dismissing mid-send and losing visibility into
    // the result toast.
    const handleClose = () => {
        if (isConfirming) return;
        onCancel?.();
    };
    const mode = useMemo(() => (emailType ? getEmailMode(emailType) : 'mode3'), [emailType]);

    const [isLoading, setIsLoading] = useState(false);
    // Subject as it would render with sample values (display only when not editing).
    const [previewSubject, setPreviewSubject] = useState('');
    // Raw template body (Handlebars placeholders intact). Seeds the editor so
    // chips appear and per-recipient substitution survives the round-trip to
    // backend. Different from the preview HTML which has values already
    // substituted — using THAT as the editor seed was the bug that caused
    // empty links in sent emails (every recipient got the same baked-in
    // sample values instead of their own data).
    const [rawBody, setRawBody] = useState('');
    const [rawSubject, setRawSubject] = useState('');
    // Pristine preview HTML for the iframe. Includes {{var}} → sample
    // substitutions so admin sees what an email looks like.
    const [iframeDoc, setIframeDoc] = useState('');
    // Edit-mode subject: starts as the rendered subject so admin sees readable
    // text but lives separately from `rawSubject` so we can tell whether the
    // admin actually changed it.
    const [editedSubject, setEditedSubject] = useState('');
    const [editedBodyChips, setEditedBodyChips] = useState('');
    const [reason, setReason] = useState('');
    // Mode 3 default is preview-only. Admin opts in to editing via toggle.
    const [isEditing, setIsEditing] = useState(false);
    const editorRef = useRef(null);
    const debounceRef = useRef(null);

    // Re-fetch only when the modal opens or the emailType changes — NOT on
    // every parent re-render. Earlier versions included `templateData` in deps;
    // callers pass a fresh object literal each render, so the effect fired
    // every keystroke and remounted the editor.
    const templateDataRef = useRef(templateData);
    templateDataRef.current = templateData;

    useEffect(() => {
        if (!isOpen || !emailType) return;
        if (mode === 'mode1') {
            // eslint-disable-next-line no-console
            console.error(`EmailReviewModal opened for Mode 1 emailType "${emailType}" — Mode 1 emails do not require review`);
            return;
        }

        setIsLoading(true);
        setPreviewSubject('');
        setRawBody('');
        setRawSubject('');
        setIframeDoc('');
        setEditedSubject('');
        setEditedBodyChips('');
        setReason('');
        setIsEditing(false);

        // Fetch BOTH the rendered preview (for the iframe display) AND the raw
        // template (for the editor seed + clean send-without-edit fallback).
        // Two endpoints, one parallel fetch — render time is not a bottleneck
        // (preview returns in ~80ms, raw returns in ~30ms).
        Promise.all([
            webApiClient.post(`/api/v2/org/email-templates/${emailType}/preview`, { templateData: templateDataRef.current || {} }),
            webApiClient.get(`/api/v2/org/email-templates/${emailType}`),
        ]).then(([previewRes, rawRes]) => {
            if (previewRes.ok && previewRes.data) {
                setPreviewSubject(previewRes.data.subject || '');
                setIframeDoc(previewRes.data.html || '');
            }
            if (rawRes.ok && rawRes.data) {
                setRawSubject(rawRes.data.subject || '');
                setRawBody(rawRes.data.bodyHtml || '');
                setEditedSubject(rawRes.data.subject || '');
            }
        }).catch(() => {}).finally(() => setIsLoading(false));
    }, [isOpen, emailType, mode]);

    // Debounce-update the preview iframe as the editor changes.
    const handleEditorChange = (html) => {
        setEditedBodyChips(html);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setIframeDoc(tokensToHandlebars(html));
        }, 500);
    };

    const handleConfirm = () => {
        if (mode === 'mode3') {
            // No-edit path: pass `undefined` for both override fields so the
            // backend uses the saved template via resolveTemplate, with all
            // {{var}} placeholders intact for per-recipient substitution.
            // Edit path: send the admin's edited values (which preserve chip
            // → handlebars placeholders because the editor was seeded with
            // the raw template, not the preview HTML).
            if (isEditing && editorRef.current) {
                const handlebarsBody = editorRef.current.getHandlebars();
                const subjectChanged = editedSubject !== rawSubject;
                onConfirm(
                    subjectChanged ? editedSubject : undefined,
                    handlebarsBody || undefined,
                );
            } else {
                onConfirm(undefined, undefined);
            }
        } else if (mode === 'mode2a') {
            onConfirm({ reason });
        } else if (mode === 'mode2b') {
            onConfirm({});
        }
    };

    const titleLabel = mode === 'mode2a'
        ? 'Reason for Cancellation'
        : mode === 'mode2b'
            ? 'Confirm Send'
            : 'Review Email Before Sending';

    const buttonLabel = mode === 'mode2b'
        ? `Send to ${recipientCount || 'all'}`
        : 'Send Email';

    const confirmDisabled = isLoading || isConfirming || (mode === 'mode2a' && !reason.trim());

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={handleClose}
            title={titleLabel}
            icon={mdiEmailOutline}
            maxWidth={mode === 'mode2a' ? 520 : 880}
            footer={
                <>
                    {warningMessage && (
                        <p className="email-review-modal__warning">{warningMessage}</p>
                    )}
                    <button className="base-modal-cancel-btn" onClick={handleClose} disabled={isConfirming}>
                        Cancel
                    </button>
                    <button
                        className={`base-modal-save-btn email-review-modal__confirm-btn${dangerConfirm ? ' email-review-modal__confirm-btn--danger' : ''}`}
                        onClick={handleConfirm}
                        disabled={confirmDisabled}
                    >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Icon path={mdiEmailFastOutline} size={0.8} />
                            {isConfirming ? (confirmingLabel || 'Sending…') : buttonLabel}
                        </span>
                    </button>
                </>
            }
        >
            {isLoading ? (
                <p className="email-review-modal__loading">Loading preview…</p>
            ) : (
                <>
                    {mode === 'mode3' && (
                        <div className="email-review-modal__content">
                            {(audienceDescription || recipientCount != null) && (
                                <div className="email-review-modal__audience-banner">
                                    <span className="email-review-modal__audience-icon">→</span>
                                    <span>
                                        {audienceDescription
                                            ? audienceDescription
                                            : `${recipientCount} recipient${recipientCount !== 1 ? 's' : ''}`}
                                        {audienceDescription && recipientCount != null && (
                                            <> ({recipientCount} recipient{recipientCount !== 1 ? 's' : ''})</>
                                        )}
                                    </span>
                                </div>
                            )}
                            <div className="email-review-modal__subject-row">
                                <span className="email-review-modal__subject-label">Subject:</span>
                                {isEditing ? (
                                    <input
                                        className="form-input email-review-modal__subject-input"
                                        value={editedSubject}
                                        onChange={e => setEditedSubject(e.target.value)}
                                    />
                                ) : (
                                    <span className="email-review-modal__subject-text">{previewSubject}</span>
                                )}
                            </div>

                            {!isEditing && (
                                <>
                                    <div className="email-review-modal__edit-toggle-row">
                                        <button
                                            type="button"
                                            className="email-review-modal__edit-toggle"
                                            onClick={() => setIsEditing(true)}
                                        >
                                            Edit this send only
                                        </button>
                                        <span className="email-review-modal__edit-hint">
                                            (Saved template changes are made in Settings → Email Templates.)
                                        </span>
                                    </div>
                                    <iframe
                                        sandbox="allow-same-origin"
                                        srcDoc={iframeDoc}
                                        className="email-review-modal__iframe"
                                        title="Email preview"
                                    />
                                </>
                            )}

                            {isEditing && (
                                <div className="email-review-modal__field">
                                    <p className="form-hint">
                                        Edit the body for this send only. Token chips like [Student Name] render with each recipient's details at send time.
                                        Use the formats menu to change the block type, or "Clear formatting" to remove inherited styles.
                                    </p>
                                    <EmailTemplateEditor
                                        ref={editorRef}
                                        emailType={emailType}
                                        initialHtml={rawBody}
                                        onChange={handleEditorChange}
                                        height={380}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                    {mode === 'mode2a' && (
                        <div className="email-review-modal__content">
                            <div className="email-review-modal__field">
                                <label className="email-review-modal__label">Reason</label>
                                <p className="form-hint">This text will be included in the email sent to attendees.</p>
                                <textarea
                                    className="form-input email-review-modal__reason-textarea"
                                    value={reason}
                                    onChange={e => setReason(e.target.value)}
                                    rows={5}
                                    placeholder="The event has been cancelled because…"
                                />
                            </div>
                        </div>
                    )}
                    {mode === 'mode2b' && (
                        <div className="email-review-modal__content">
                            <p className="form-hint">
                                {recipientCount
                                    ? `${recipientCount} student${recipientCount !== 1 ? 's' : ''} will receive this email. Each student receives the email with their first name substituted.`
                                    : 'This email will be sent now. Review the contents below before confirming.'}
                            </p>
                            <iframe
                                sandbox="allow-same-origin"
                                srcDoc={iframeDoc}
                                className="email-review-modal__iframe"
                                title="Email preview"
                            />
                        </div>
                    )}
                </>
            )}
        </BaseModal>
    );
};

export default EmailReviewModal;
